import { useCallback, useEffect, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { useAtomValue } from 'jotai';

import { DirectMessageService } from '@/components/pages/conversations/SocialInbox/services/DirectMessageService';

import { useDMConversationMessages } from '@/utils/hooks/swr/conversations/useDMConversationMessages';
import { useSWRSocket, WEBSOCKET_EVENTS } from '@/utils/hooks/swr/useSWRSocket';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

import {
    DMConversation,
    DMConversationRefreshEvent,
    DMInboundMessageEvent
} from '@/components/pages/conversations/SocialInbox/types';

import { openedMessageAtom } from '@/components/pages/conversations/SocialInbox/atoms';

import {
    InfiniteScroll,
    InfiniteScrollRef
} from '@/components/composite/InfiniteScroll/InfiniteScroll';
import { DirectMessageBubble } from '@/components/pages/conversations/SocialInbox/components/SocialInboxChatView/SocialInboxMessages/SocialInboxDirectMessages/DirectMessageBubble/DirectMessageBubble';
import { DirectMessageInput } from '@/components/pages/conversations/SocialInbox/components/SocialInboxChatView/SocialInboxMessages/SocialInboxDirectMessages/DirectMessageInput/DirectMessageInput';
import { SocialInboxDirectMessagesSkeleton } from '@/components/pages/conversations/SocialInbox/components/SocialInboxChatView/SocialInboxMessages/SocialInboxDirectMessages/SocialInboxDirectMessagesSkeleton';

export const SocialInboxDirectMessages = () => {
    const openedConversation = useAtomValue(openedMessageAtom) as DMConversation;
    const { activeSocialProfile } = useActiveSocialProfile();
    const { data, isLoading, isLoadingMore, isLoadMore, loadMore } = useDMConversationMessages({
        activeSPID: activeSocialProfile?.id,
        conversationId: openedConversation.meta_conversation_id,
        options: {
            revalidateIfStale: true
        }
    });
    const { data: event } = useSWRSocket<DMInboundMessageEvent | DMConversationRefreshEvent>();

    const formMethods = useForm({ mode: 'all', defaultValues: { message: '' } });
    const infiniteScroll = useRef<InfiniteScrollRef>(null);
    const previousConversationId = useRef<string | null>(null);
    const previousDataLength = useRef<number>(0);

    const handleScrollToBottom = useCallback(() => {
        if (infiniteScroll.current) {
            // Small delay to ensure DOM is updated before scrolling
            requestAnimationFrame(() => {
                infiniteScroll.current?.scrollToBottom();
            });
        }
    }, []);

    // Scroll to bottom on conversation switch
    useEffect(() => {
        if (openedConversation.meta_conversation_id !== previousConversationId.current) {
            previousConversationId.current = openedConversation.meta_conversation_id;
            previousDataLength.current = 0;

            // Scroll to bottom when switching conversations
            if (!isLoading && data.length > 0) {
                handleScrollToBottom();
            }
        }
    }, [openedConversation.meta_conversation_id, isLoading, data.length, handleScrollToBottom]);

    // Scroll to bottom when new messages arrive (not when loading more old messages)
    useEffect(() => {
        if (!isLoading && data.length > 0) {
            // Only scroll to bottom if:
            // 1. Initial load (previousDataLength was 0)
            // 2. New message added (data.length increased but not by a page load amount)
            const isInitialLoad = previousDataLength.current === 0;
            const isNewMessageAdded =
                data.length > previousDataLength.current &&
                data.length - previousDataLength.current < 5; // Less than a page of messages

            if (isInitialLoad || isNewMessageAdded) {
                handleScrollToBottom();
            }

            previousDataLength.current = data.length;
        }
    }, [data.length, isLoading, handleScrollToBottom]);

    // Scroll to bottom on websocket event (new incoming message)
    useEffect(() => {
        if (
            event?.event === WEBSOCKET_EVENTS.INSTAGRAM_INBOUND_MESSAGE &&
            event?.data?.message_data?.meta_conversation_id ===
                openedConversation.meta_conversation_id
        ) {
            handleScrollToBottom();
        }
    }, [event, openedConversation.meta_conversation_id, handleScrollToBottom]);

    return (
        <FormProvider {...formMethods}>
            {isLoading ? (
                <SocialInboxDirectMessagesSkeleton />
            ) : (
                <InfiniteScroll
                    ref={infiniteScroll}
                    data={data}
                    loadMore={loadMore}
                    hasMore={isLoadMore}
                    isLoadingMore={isLoadingMore}
                    className="px-4 sm:px-10 [&>div>div:last-child]:pb-4 sm:[&>div>div:last-child]:pb-10"
                    renderItem={(message, index) => {
                        const previousMessage = index > 0 ? data[index - 1] : null;
                        const isGroupedWithPrevious = DirectMessageService.shouldGroupWithPrevious(
                            message,
                            previousMessage
                        );

                        return (
                            <DirectMessageBubble
                                key={message.id}
                                message={message}
                                isGroupedWithPrevious={isGroupedWithPrevious}
                                className={isGroupedWithPrevious ? 'mt-1' : 'mt-10'}
                            />
                        );
                    }}
                    inverse
                />
            )}

            <DirectMessageInput onSend={handleScrollToBottom} disabled={isLoading} />
        </FormProvider>
    );
};
